import { useEffect, useRef, useState } from 'react';
import type { CellAlert } from '@liveline/ll_node_common/lib/models/websocket';
import useApiClient from '../services';
import { buildTagCatalogue, CellTagCatalogue } from '../utils/alertSensor';

// Aliases are edited in the admin Equipment & Sensor editor and show on the tree at once
// (it reads a plain view), so a long-lived tab re-reads a cell now and then rather than once.
const REFRESH_MS = 10 * 60_000;
// A failed cell is retried sooner — the row meanwhile falls back to the alert's own alias.
const RETRY_MS = 60_000;

/**
 * LT-3320 — the sensor catalogue the cell-alerts feed names its rows from, one equipment
 * tree per cell.
 *
 * Fetched lazily, and only for cells that have a tag-scoped alert in the feed: the feed is
 * facility-wide, but most cells most of the time have no alert naming a sensor, so fanning
 * out over every cell would fetch trees nobody reads. Keyed by cell id within the current
 * facility; switching facility drops everything, because another facility is another
 * database.
 */
export const useAlertTagCatalogue = (facilityId: number, alerts: CellAlert[]): Map<number, CellTagCatalogue> => {
    const client = useApiClient();
    const [catalogues, setCatalogues] = useState<Map<number, CellTagCatalogue>>(() => new Map());
    // When each cell was last asked for, and whether that ask failed.
    const requestedRef = useRef(new Map<number, { at: number; failed: boolean }>());
    const facilityRef = useRef(facilityId);

    useEffect(() => {
        facilityRef.current = facilityId;
        requestedRef.current = new Map();
        setCatalogues(new Map());
    }, [facilityId]);

    // The client is rebuilt on every token refresh (useApiClient keys it on the token), so it
    // is a dependency only so a fetch can start once one exists — the cache is never reset on it.
    useEffect(() => {
        if (!client || !facilityId) return;
        const now = Date.now();
        const cellIds = new Set<number>();
        alerts.forEach((a) => {
            if (typeof a?.tag_id === 'number' && a.facility_line_id) cellIds.add(a.facility_line_id);
        });
        cellIds.forEach((cellId) => {
            const last = requestedRef.current.get(cellId);
            if (last && now - last.at < (last.failed ? RETRY_MS : REFRESH_MS)) return;
            requestedRef.current.set(cellId, { at: now, failed: false });
            const forFacility = facilityId;
            // A refresh keeps showing the names it already has; only a first ask is 'loading'.
            setCatalogues((prev) => (prev.has(cellId) ? prev : new Map(prev).set(cellId, { status: 'loading' })));
            client
                .fetchCellEquipmentTree(cellId)
                .then((tree) => {
                    if (facilityRef.current !== forFacility) return;
                    if (!tree) {
                        requestedRef.current.set(cellId, { at: Date.now(), failed: true });
                        setCatalogues((prev) =>
                            prev.get(cellId)?.status === 'ready' ? prev : new Map(prev).set(cellId, { status: 'failed' }),
                        );
                        return;
                    }
                    setCatalogues((prev) =>
                        new Map(prev).set(cellId, { status: 'ready', tags: buildTagCatalogue(tree.equipment) }),
                    );
                });
        });
    }, [alerts, client, facilityId]);

    return catalogues;
};

export default useAlertTagCatalogue;
